import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';

const ArchiveMetrics = () => {
  const [metrics, setMetrics] = useState([]);
  const [stats, setStats] = useState({ total: 0, success: 0, partial: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [userFilter, setUserFilter] = useState('');

  useEffect(() => {
    console.log('ArchiveMetrics component mounted');
    fetchMetrics();
  }, []);

  const fetchMetrics = async (userSub = null) => {
    console.log('Fetching metrics...');
    setLoading(true);
    setError(null);
    
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      console.log('Got token, making API call...');
      
      const queryParams = userSub ? `?userSub=${userSub}` : '';
      const url = `https://v6d18bgl57.execute-api.us-west-1.amazonaws.com/prod/admin-metrics${queryParams}`;
      console.log('Fetching from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      console.log('Received data:', data);
      setMetrics(data.metrics || []);
      setStats(data.stats || { total: 0, success: 0, partial: 0, failed: 0 });
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${(seconds / 60).toFixed(1)}m`;
  };

  const formatSize = (mb) => {
    if (!mb) return 'N/A';
    if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
    return `${mb.toFixed(1)} MB`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: 'badge bg-success',
      partial: 'badge bg-warning',
      failed: 'badge bg-danger'
    };
    return badges[status] || 'badge bg-secondary';
  };

  const handleUserFilter = (e) => {
    e.preventDefault();
    fetchMetrics(userFilter || null);
  };

  console.log('Rendering ArchiveMetrics, loading:', loading, 'metrics:', metrics.length);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <h2 className="mb-4">Archive Metrics</h2>
      
      {/* Stats Summary */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title">Total Archives</h5>
              <h3 className="text-primary">{stats.total}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title">Successful</h5>
              <h3 className="text-success">{stats.success}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title">Partial</h5>
              <h3 className="text-warning">{stats.partial}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title">Failed</h5>
              <h3 className="text-danger">{stats.failed}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* User Filter */}
      <div className="row mb-3">
        <div className="col-md-6">
          <form onSubmit={handleUserFilter} className="d-flex">
            <input
              type="text"
              className="form-control me-2"
              placeholder="Filter by User Sub ID (leave empty for all)"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            />
            <button type="submit" className="btn btn-primary">Filter</button>
            <button 
              type="button" 
              className="btn btn-secondary ms-2"
              onClick={() => { setUserFilter(''); fetchMetrics(); }}
            >
              Clear
            </button>
          </form>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          Error loading metrics: {error}
        </div>
      )}

      {metrics.length === 0 && !loading && !error && (
        <div className="alert alert-info" role="alert">
          No metrics found. Archive something to see metrics here.
        </div>
      )}

      {/* Metrics Table */}
      {metrics.length > 0 && (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="table-dark">
              <tr>
                <th>Timestamp</th>
                <th>Status</th>
                <th>URL</th>
                <th>Plan</th>
                <th>Files</th>
                <th>Size</th>
                <th>Duration</th>
                <th>Completion</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric) => (
                <tr key={metric.metric_id}>
                  <td>{new Date(metric.timestamp).toLocaleString()}</td>
                  <td>
                    <span className={getStatusBadge(metric.status)}>
                      {metric.status}
                    </span>
                  </td>
                  <td>
                    <small>{metric.hostname}</small>
                  </td>
                  <td>{metric.user_plan}</td>
                  <td>
                    {metric.metrics?.counts?.files_uploaded || 0} / {metric.metrics?.counts?.urls_discovered || 0}
                  </td>
                  <td>{formatSize(metric.metrics?.counts?.total_size_mb)}</td>
                  <td>{formatDuration(metric.metrics?.duration?.total)}</td>
                  <td>
                    <div className="progress" style={{ minWidth: '60px' }}>
                      <div 
                        className="progress-bar" 
                        role="progressbar" 
                        style={{ width: `${Math.min(100, metric.metrics?.completion_percentage || 0)}%` }}
                        aria-valuenow={metric.metrics?.completion_percentage || 0}
                        aria-valuemin="0" 
                        aria-valuemax="100"
                      >
                        {(metric.metrics?.completion_percentage || 0).toFixed(0)}%
                      </div>
                    </div>
                  </td>
                  <td>
                    <button 
                      className="btn btn-sm btn-info"
                      onClick={() => setSelectedMetric(metric)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedMetric && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Archive Details - {selectedMetric.hostname}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedMetric(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>General Info</h6>
                    <ul className="list-unstyled">
                      <li><strong>URL:</strong> {selectedMetric.initial_url}</li>
                      <li><strong>User:</strong> {selectedMetric.user_sub}</li>
                      <li><strong>Plan:</strong> {selectedMetric.user_plan}</li>
                      <li><strong>Status:</strong> <span className={getStatusBadge(selectedMetric.status)}>{selectedMetric.status}</span></li>
                      <li><strong>Has Sitemap:</strong> {selectedMetric.metrics?.counts?.has_sitemap ? 'Yes' : 'No'}</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <h6>Performance</h6>
                    <ul className="list-unstyled">
                      <li><strong>Discovery:</strong> {formatDuration(selectedMetric.metrics?.duration?.discovery)}</li>
                      <li><strong>Download:</strong> {formatDuration(selectedMetric.metrics?.duration?.download)}</li>
                      <li><strong>Processing:</strong> {formatDuration(selectedMetric.metrics?.duration?.processing)}</li>
                      <li><strong>Upload:</strong> {formatDuration(selectedMetric.metrics?.duration?.upload)}</li>
                      <li><strong>Total:</strong> {formatDuration(selectedMetric.metrics?.duration?.total)}</li>
                    </ul>
                  </div>
                </div>
                {selectedMetric.metrics?.error && (
                  <div className="alert alert-danger mt-3">
                    <strong>Error:</strong> {selectedMetric.metrics.error}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedMetric(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchiveMetrics;
